import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { SOURCE_LABEL } from "@/lib/constants";
import { toast } from "sonner";

export default function ClientEditDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        full_name: client.full_name || "",
        email: client.email || "",
        phone: client.phone || "",
        city: client.city || "",
        birth_date: client.birth_date || "",
        source: client.source || "web_form",
        tags: client.tags || [],
        consent: !!client.consent,
      });
    }
  }, [client]);

  if (!form) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/clients/${client.id}`, form);
      toast.success("Danışan güncellendi");
      onSaved(data);
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="client-edit-dialog">
        <DialogHeader>
          <DialogTitle>Danışanı Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Ad Soyad *</label>
            <input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="edit-client-name"
              className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">E-posta</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="edit-client-email"
                className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Telefon</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="edit-client-phone"
                className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Şehir</label>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="edit-client-city"
                className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Doğum Tarihi</label>
              <input
                type="date"
                value={form.birth_date ? form.birth_date.slice(0, 10) : ""}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                data-testid="edit-client-birthdate"
                className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Kaynak</label>
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              data-testid="edit-client-source"
              className="w-full h-9 px-2 border border-pleiades-border rounded-sm text-[13px] bg-white focus:outline-none focus:border-pleiades-blue"
            >
              {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Etiketler</label>
            <div className="flex gap-1.5 flex-wrap mb-1.5">
              {form.tags.map((t) => (
                <span key={t} className="text-[11px] px-2 py-0.5 bg-pleiades-gold-soft text-[#8a6f37] border border-pleiades-gold/30 rounded-sm inline-flex items-center gap-1">
                  {t}
                  <button type="button" onClick={() => removeTag(t)} className="text-[#8a6f37] hover:text-red-600">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Etiket ekle ve Enter"
                data-testid="edit-client-tag-input"
                className="flex-1 h-8 px-2 border border-pleiades-border rounded-sm text-[12px]"
              />
              <button type="button" onClick={addTag} className="h-8 px-2 border border-pleiades-border rounded-sm text-[12px] hover:bg-pleiades-sidebar">
                Ekle
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              data-testid="edit-client-consent"
            />
            <span className="text-pleiades-slate">KVKK onayı verildi.</span>
          </label>

          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 px-3 border border-pleiades-border rounded-sm text-[13px] hover:bg-pleiades-sidebar">
              Vazgeç
            </button>
            <button type="submit" disabled={busy} data-testid="edit-client-submit" className="h-9 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[13px] rounded-sm disabled:opacity-50">
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
